/**
 * Lowering — logical blocks (`macro/…` pseudo-nodes) to their tagged
 * canonical expansions. The inverse of `raiseMacros` (recognize.ts).
 */

import type { BehaviorGraphData, FlowTarget, GraphData, GraphNode } from '../../../scenedata/behaviors/behaviorgraphdata';
import { getMacroByOp, MACRO_ID_KEY, TRACE_MACRO_KEY, type MacroDefinition, type MacroRoleNodes, type MacroWiredValue } from './registry';
import { emitMacroInstance, pruneDeclarations, remapNodeRefs } from './graphEdit';
import { raiseMacros } from './recognize';

/**
 * Lower macro pseudo-nodes into their canonical expansions.
 *
 * High-level authoring (the AI assistant writes GraphData JSON; the editor
 * collapses expansions into blocks) emits a single node whose declaration op
 * is a catalog pseudo-op, e.g. `macro/interval`. Pseudo-ops are names for
 * logical blocks, not runtime ops, and never reach stored GraphData; this pass
 * rewrites each pseudo-node into the tagged expansion `expandMacro` emits,
 * routing its sockets by the macro's declared `inputs`/`outputs`/`params`.
 * Each expansion takes its pseudo-node's place in node order, so raising and
 * re-lowering a graph leaves it where it was.
 *
 * Stored expansions in a superseded shape (`MacroDefinition.legacyShapes`) are
 * raised first, so they are re-lowered in the current shape at this same
 * choke point.
 *
 * Idempotent and tolerant: a graph without pseudo-ops or superseded
 * expansions is returned unchanged (same reference). Unregistered `macro/…`
 * ops are left as-is — they fail compilation like any unknown op, which is the
 * honest outcome for a pseudo-op this build doesn't know how to expand.
 *
 * @returns A new lowered `GraphData` (the input is not mutated), or the input
 *   itself when there is nothing to lower.
 */
export function lowerMacros(authored: GraphData): GraphData {
    const graph = raiseMacros(authored, { legacyOnly: true });
    if (!graph.declarations.some(d => getMacroByOp(d.op) !== undefined)) return graph;

    const out = structuredClone(graph);

    // Instance ids already present in the graph (existing tagged expansions).
    // An authored id must not collide with one of these — duplicate roles per
    // id read as an incoherent group and degrade BOTH instances.
    const usedIds = new Set<string>();
    for (const node of out.nodes) {
        const id = node.metadata?.[TRACE_MACRO_KEY] !== undefined ? node.metadata?.[MACRO_ID_KEY] : undefined;
        if (typeof id === 'string') usedIds.add(id);
    }

    // 1. Append one canonical expansion per pseudo-node (indices stable).
    interface Replacement {
        def: MacroDefinition;
        nodes: MacroRoleNodes;
    }
    const replacements = new Map<number, Replacement>();
    const pseudoCount = out.nodes.length;
    for (let i = 0; i < pseudoCount; i++) {
        const node = out.nodes[i];
        const def = getMacroByOp(out.declarations[node.declaration]?.op ?? '');
        if (!def) continue;

        // A pseudo-node re-emitted by a raise carries the instance's id in its
        // metadata — reuse it so the id is stable across raise → lower round
        // trips instead of churning on every save. Fresh (or colliding)
        // authoring gets a new id.
        const authoredId = node.metadata?.[MACRO_ID_KEY];
        const macroId = typeof authoredId === 'string' && authoredId !== '' && !usedIds.has(authoredId) ? authoredId : undefined;
        const expansion = emitMacroInstance(out, def, pseudoNodeContent(def, node), macroId);
        usedIds.add(expansion.macroId);

        // Author metadata (editor position etc.) moves to the entry node; the
        // expansion's macro tags win on collision.
        if (node.metadata) {
            const entryNode = out.nodes[expansion.nodes[def.entryRole]];
            entryNode.metadata = { ...node.metadata, ...entryNode.metadata };
        }
        replacements.set(i, { def, nodes: expansion.nodes });
    }

    // 2. Rewire inbound flows (including flows copied in step 1, which may
    // target another pseudo-node) to the macro's declared inputs.
    for (const node of out.nodes) {
        for (const [key, target] of Object.entries(node.flows ?? {})) {
            const replacement = replacements.get(target.node);
            if (!replacement) continue;
            const { def, nodes } = replacement;
            const ref = def.inputs[target.socket] ?? def.inputs[def.defaultInput];
            node.flows![key] = { node: nodes[ref.role], socket: ref.socket };
        }
    }
    // A subgraph interface entering a block lands on its declared input;
    // one leaving a block leaves from its declared output.
    for (const socket of Object.values(out.inputFlowSockets ?? {})) {
        const replacement = replacements.get(socket.node);
        if (!replacement) continue;
        const { def, nodes } = replacement;
        const ref = def.inputs[socket.socket] ?? def.inputs[def.defaultInput];
        Object.assign(socket, { node: nodes[ref.role], socket: ref.socket });
    }
    for (const socket of Object.values(out.outputFlowSockets ?? {})) {
        const replacement = replacements.get(socket.node);
        const ref = replacement?.def.outputs[socket.socket];
        if (replacement && ref) Object.assign(socket, { node: replacement.nodes[ref.role], socket: ref.socket });
    }

    // 3. Put each expansion where its pseudo-node was and compact. A removed
    // pseudo-node maps to its expansion's entry, so any reference not covered
    // by the socket rewiring above (e.g. a value-ref, which a macro cannot
    // legally be the source of) still points at the replacement.
    const order: number[] = [];
    for (let i = 0; i < pseudoCount; i++) {
        const replacement = replacements.get(i);
        if (replacement) order.push(...replacement.def.nodes.map(n => replacement.nodes[n.role]));
        else order.push(i);
    }
    const indexMap = new Map(order.map((old, index) => [old, index]));
    for (const [i, { def, nodes }] of replacements) indexMap.set(i, indexMap.get(nodes[def.entryRole])!);
    out.nodes = order.map(i => out.nodes[i]);
    remapNodeRefs(out, indexMap);

    // 4. The pseudo-op declarations are now unreferenced.
    pruneDeclarations(out);
    return out;
}

/** A pseudo-node's block content: literal/config params, wired params, outbound flows. */
function pseudoNodeContent(
    def: MacroDefinition,
    node: GraphNode,
): { literals: Record<string, unknown>; wired: Record<string, MacroWiredValue>; outputs: Record<string, FlowTarget> } {
    // Inline literals expand through the shared path (so validation and
    // disarming are the fluent path's); wired params carry the node-ref
    // straight onto the target sockets.
    const literals: Record<string, unknown> = {};
    const wired: Record<string, MacroWiredValue> = {};
    for (const [name, param] of Object.entries(def.params)) {
        if (param.kind === 'config') {
            // Addressing params live in configuration and are never wired.
            const config = node.configuration?.[name];
            if (config && config.value.length > 0) literals[name] = config.value[0];
            continue;
        }
        const value = node.values?.[name];
        // An ABSENT param is left out of `literals` so `expandMacro`
        // validates it as the missing value it is — dropping it silently
        // here is how a live expansion with an unset socket gets emitted.
        if (!value) continue;
        if ('node' in value) wired[name] = { ...value };
        else if (value.value.length > 0) literals[name] = value.value[0];
    }
    const outputs: Record<string, FlowTarget> = {};
    for (const name of Object.keys(def.outputs)) {
        const flow = node.flows?.[name];
        if (flow) outputs[name] = { ...flow };
    }
    return { literals, wired, outputs };
}

/**
 * Lower every graph carried by a `BehaviorGraphData` (single-graph `graph`
 * and multi-graph `graphs` alike). Returns the input unchanged (same
 * reference) when nothing needed lowering; otherwise a new object — the
 * input is never mutated, so frozen (store-held) data is safe to pass.
 */
export function lowerBehaviorGraphData(data: BehaviorGraphData): BehaviorGraphData {
    const graph = data.graph ? lowerMacros(data.graph) : data.graph;
    const graphs = data.graphs ? data.graphs.map(g => lowerMacros(g)) : data.graphs;
    const unchanged = graph === data.graph && (graphs === data.graphs || graphs!.every((g, i) => g === data.graphs![i]));
    if (unchanged) return data;
    return {
        ...data,
        ...(graph !== undefined ? { graph } : {}),
        ...(graphs !== undefined ? { graphs } : {}),
    };
}
